import Desktop from 'components/Header/desktop';
import { SETTINGS_DROPDOWN_ITEM } from 'components/Header/HeaderDropdown';
import Mobile from 'components/Header/mobile';
import Tablet from 'components/Header/tablet';
import { useAuth } from 'contexts/auth';
import { useDialog } from 'contexts/dialog';
import { useViewport } from 'contexts/viewport';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

const getNavOptions = (path, role, t) => {
    if (!role) {
        return [
            {
                key: 'lobby',
                text: t('nav.lobby', 'Lobby'),
                link: '/',
                icon: 'home',
            },
            {
                key: 'rubrics',
                text: t('nav.rubrics', 'Rubrics'),
                link: '/rubrics',
                icon: 'dashboard',
            },
        ];
    }

    return path === '/'
        ? [
              {
                  key: 'lobby',
                  text: t('nav.lobby', 'Lobby'),
                  link: '/',
                  icon: 'home',
              },
              {
                  key: 'rubrics',
                  text: t('nav.rubrics', 'Rubrics'),
                  link: '/rubrics',
                  icon: 'dashboard',
              },
          ]
        : {
              instructor: [
                  {
                      key: 'lobby',
                      text: t('nav.lobby', 'Lobby'),
                      link: '/',
                      icon: 'home',
                  },
                  {
                      key: 'assignments',
                      text: t('nav.assignments', 'Assignments'),
                      link: '/assignments',
                      icon: 'task',
                  },
                  {
                      key: 'rubrics',
                      text: t('nav.rubrics', 'Rubrics'),
                      link: '/rubrics',
                      icon: 'dashboard',
                  },
                  {
                      key: 'people',
                      text: t('nav.people', 'People'),
                      link: '/people',
                      icon: 'teams',
                  },
              ],
          }[role];
};

const getDropdownOptions = (hasCourseSelected, t) => {
    const universalDropdownOptions = [
        { key: 'user' },
        { key: 'divider' },
        { key: SETTINGS_DROPDOWN_ITEM.PROFILE, value: t('header.account-settings', 'Account Settings') },
    ];

    return [
        ...universalDropdownOptions,
        ...(hasCourseSelected
            ? [
                  {
                      key: SETTINGS_DROPDOWN_ITEM.COURSE_SETTINGS,
                      value: t('header.course-settings', 'Course Settings'),
                  },
                  { key: SETTINGS_DROPDOWN_ITEM.PREFERENCES, value: t('header.preferences', 'Preferences') },
                  {
                      key: SETTINGS_DROPDOWN_ITEM.SWITCH_COURSE,
                      value: t('header.switch-course', 'Switch Course'),
                  },
              ]
            : []),
        { key: 'divider' },
        { key: 'logout', value: t('controls.log-out', 'Log Out') },
    ];
};

const Header = () => {
    const { auth, logout } = useAuth();
    const { setDialog, closeDialog } = useDialog();
    const location = useLocation();
    const { device } = useViewport();
    const { t } = useTranslation();

    const hasCourseSelected = Boolean(auth.owner_account_id);
    const navOptions = getNavOptions(location.pathname, auth.role, t);
    const dropdownOptions = getDropdownOptions(hasCourseSelected, t);

    const handleLogout = () => {
        setDialog({
            open: true,
            message: t('dialog.logout-confirmation.title', 'Logging Out?'),
            description: t(
                'dialog.logout-confirmation.description',
                'Are you sure you would like to log out of TimelyGrader?'
            ),
            note: '',
            variant: 'destructive',
            actionButtonText: t('controls.log-out', 'Log Out'),
            cancelButtonText: t('controls.cancel', 'Cancel'),
            onAction: () => {
                logout({ isExpired: false });
                closeDialog();
            },
            onCancel: () => closeDialog(),
            onClose: () => closeDialog(),
        });
    };

    const props = {
        hasCourseSelected,
        navOptions,
        dropdownOptions,
        logout: handleLogout,
    };

    const renderResponsiveLayout = (device) => {
        return {
            mobile: <Mobile {...props} />,
            tablet: <Tablet {...props} />,
            desktop: <Desktop {...props} />,
        }[device];
    };

    return isEmpty(auth) ? null : renderResponsiveLayout(device);
};

export default Header;
